import {
    Box, Container, Paper
} from "@mui/material";
import { Fab, Grid, IconButton, makeStyles, TextField, Theme, Typography } from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import DealerAPI from 'components/authentication/api/dealer-api';
import { store, useAppSelector } from 'app';
import React from "react";
import AppSnackbar from 'components/snackbar/snackbar';
import useLocations from 'common/hooks/useLocations';
import AutomationAPI, { AddUpdateAutomationSettingRequest, AutomationFilter, AutomationGetDepartmentsRequest, AutomationSetting, AutomationSettingListingRef, DepartmentPhoneResponse, DepartmentResponse, GetAutomationSettingRequest, GetWorkOrderStatusesRequest, GetWorkOrderStatusesResponse } from '../api/automation-api';
import { BaseResponse } from 'common/utils/shared/shared-models';
import { allowedCategories, AutomationConstant } from '../api/automation-constants';
import useUsersList from 'components/setting/components/department/userUsersList';
import taskboardApi from 'components/lead/apis/taskboard-api';
import AddEditAutomation from './add-update-form/automation-add-update-form';
import { TaskType } from "components/lead/models/task";
import AutomationSettingListing from "./automation-settings-listing";
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@material-ui/icons/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import useDebounce from "common/hooks/useDebounce";
import AutomationFilterDrawer from "./filter-form/automation-filter";
import axios, { CancelTokenSource } from "axios";


const useStyles = makeStyles((theme: Theme): any => ({

    primaryAddBtn: {
        background: theme.palette.primary.main,
        color: theme.palette.common.white,
        '&:hover': {
            background: theme.palette.primary.dark,
        },
        [theme.breakpoints.down('xs')]: {
            height: 48,
        },
    },
}));

function TextAutomation(): React.ReactElement {
    const classes: any = useStyles();
    const [showSnackbar] = AppSnackbar.useSnackBar();
    const { dmsDealerId, dealerId } = useAppSelector((state) => state.user);

    // #region Automation Settings State

    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
    const [isFilterOpen, setIsFilterOpen] = React.useState(false);

    const [deleteBtnLoading, setDeleteBtnLoading] = React.useState(false);
    const [addUpdateLoading, setAddUpdateLoading] = React.useState(false);

    const [searchText, setSearchText] = React.useState('');

    const dealerUsers = useUsersList(dealerId);
    const { loading: locationLoading, locationOptions, defaultLocationOption } = useLocations(dmsDealerId);
    const [taskTypes, setTaskTypes] = React.useState<TaskType[]>([]);
    const [addEditSetting, setAddEditSetting] = React.useState(false);
    const [dataWOStatus, SetDataWOStatus] = React.useState<Array<GetWorkOrderStatusesResponse> | undefined>([]);
    const [departments, setDepartments] = React.useState<Array<DepartmentResponse>>();

    const [addUpdateSetting, setAddUpdateSetting] = React.useState<any>([]);

    const [automationFilters, setAutomationFilters] = useState<AutomationFilter>({
        automationTypeIds: [],
        locationDealerIds: [],
        departmentIds: [],
        statusIds: [],
        workOrderStatusIds: [],
    });

    const listingRef = useRef<AutomationSettingListingRef>(null);

    // #endregion Automation Settings State

    const getTaskTypeList = React.useCallback(async (getTaskTypeToken:CancelTokenSource): Promise<void> => {
        try {

            const data: TaskType[] = await taskboardApi.getTaskTypes(getTaskTypeToken.token);

            const filteredTasks = data.filter(task =>
                task.taskCategory && allowedCategories.includes(task.taskCategory.value.toLowerCase())
            );

            setTaskTypes(filteredTasks);
        } catch (error) {
            //if (axios.isCancel(error)) return;
            console.error("Error fetching task types:", error);
        }
    }, []);

    useEffect(() => {
        if (locationOptions?.length) {
            GetAllDepartments();
        }
    }, [locationOptions]);

    useEffect(() => {
         const getTaskTypeToken = axios.CancelToken.source();
        getTaskTypeList(getTaskTypeToken);
        return () => {
            getTaskTypeToken.cancel('Request canceled due to component unmount or parameter change.');
        };
    }, []);

    useEffect(() => {
        const getworkOrderToken = axios.CancelToken.source();
        GetWorkOrderStatuses(getworkOrderToken);
        return () => {
            getworkOrderToken.cancel('Request canceled due to component unmount or parameter change.');
        };
    }, []);

    // #region Edit Drawer

    async function AddSetting() {

        const defaultSetting: AutomationSetting = {
            id: 0,
            dealerId: 0,
            departmentId: 0,
            locationName: "",
            departmentName: "",
            orderStatus: "",
            orderStatusId: "",
            startingHour: 8,
            endingHour: 19,
            delayMessage: 0,

            isText: true,
            textMessage: AutomationConstant.DefaultMessage,

            isEmail: true,
            emailSubject: "",
            emailMessage: AutomationConstant.DefaultMessage,
            emailPreHeader: "",

            isTask: true,
            taskSubject: "",
            taskTypeId: undefined,
            taskDueDays: 0,
            taskNotes: "",
            assignedUserId: "",

            isSurvey: false,
            isActive: false,
            createdBy: "",
            creationDate: new Date(),
            updatedBy: "",
            updationDate: new Date(),
        };

        setAddUpdateSetting(defaultSetting);
        OpenEditDrawer();
    }

    function EditSetting(row: AutomationSetting) {
        setAddUpdateSetting(row);
        OpenEditDrawer()
    }

    const OpenEditDrawer = () => {
        setIsDrawerOpen(true);
        setAddEditSetting(true);
    }

    const closeEditDrawer = () => {
        setIsDrawerOpen(false);
        setAddEditSetting(false);
        setAddUpdateSetting(null);
    }

    // #endregion Edit Drawer

    // #region Automation Settings CRUD

    const handleError = (error: any, fallbackMessage: string): void => {
        const errorMessage = error?.errorDetails?.[0] || error?.message || error?.error || fallbackMessage;
        showSnackbar({ type: 'error', message: errorMessage });
    };

    const AddUpadteAutomationSetting = async (values: any): Promise<void> => {

        const addUpdateRequest: AddUpdateAutomationSettingRequest = { ...values };

        try {
            setAddUpdateLoading(true);

            let response = await AutomationAPI.AddUpdateAutomationSetting(addUpdateRequest);
            if (!response.isSuccess) {
                handleError(response, AutomationConstant.unableToSaveSetting);
                setAddUpdateLoading(false);
                return;
            }

            showSnackbar({ type: 'success', message: "Automation has been saved successfuly." });
            listingRef.current?.reload();
            closeEditDrawer();
        }
        catch (error: any) {
            handleError(error, AutomationConstant.unableToSaveSetting);
        }
        finally {

            setAddUpdateLoading(false);
        }
    }

    const DeleteAutomationSetting = async (id: number): Promise<void> => {
        setDeleteBtnLoading(true);

        try {
            const response = await AutomationAPI.DeleteAutomationSetting(id);

            if (response.isSuccess) {
                showSnackbar({ type: 'success', message: "Automation has been deleted successfully." });
                listingRef.current?.reload();
                closeEditDrawer();
            } else {
                handleError(response, AutomationConstant.unableToDeleteSetting);
            }
        } catch (error: any) {
            handleError(error, AutomationConstant.unableToDeleteSetting);
        } finally {
            setDeleteBtnLoading(false);
        }
    };


    // #endregion Automation Settings CRUD

    // #region Lookup Tables


    async function GetAllDepartments(): Promise<void> {

        let locIds = locationOptions?.filter((item: any) => item.dealerLocationId > 0);
        let promises = locIds?.map(async (item: any) => {
            
            let departmentReq: AutomationGetDepartmentsRequest = {
                dealerId: item.dealerLocationId
            }
            const response: BaseResponse<Array<DepartmentPhoneResponse>> = await AutomationAPI.GetAutomationDepartments(departmentReq);
            
            if (!response.isSuccess) {
                return {
                    dealerLocationId: item.dealerLocationId,
                    Departments: [],
                };
            }
            return {
                dealerLocationId: item.dealerLocationId,
                Departments: [...response.data],
            };
        }) || [];
        const fulfilledPromises = await Promise.all(promises);
        setDepartments(fulfilledPromises)
    }

    async function GetWorkOrderStatuses(workOrdToken:CancelTokenSource): Promise<void> {

        try {
            let workOrderReq: GetWorkOrderStatusesRequest = {
                dealerId: (store.getState().user as { dealerId: number }).dealerId,
            }

            const response: BaseResponse<Array<GetWorkOrderStatusesResponse>> = await AutomationAPI.GetWorkOrderStatuses(workOrderReq,workOrdToken.token);
            if (!response.isSuccess) {
                handleError(response, AutomationConstant.unableToFetchWorderOrderStatus);
                return;
            }

            if (!response.data || response.data.length == 0) {
                showSnackbar({ type: 'error', message: "Work Order Job statuses are missing. Without configuring Work Order Job status, automation will not work." });
            }
            else {
                SetDataWOStatus(response.data);
            }

        } catch (error: unknown) {
            if (axios.isCancel(error)) return;
            handleError(error, AutomationConstant.unableToFetchWorderOrderStatus);
        }
    }
    // #endregion Lookup Tables

    const handleSearch = (searchString: string): void => {
        setSearchText(searchString);
    }

    const debouncedSearchText = useDebounce(searchText, 1000);

    return (
        <>
            <Box>
                <Grid container >
                    <Grid item xs={2} sm={1}>
                         <Fab
                            type="submit"
                            onClick={AddSetting}
                            title="New"
                            color="primary">
                            <Box sx={{ flexDirection: 'column', alignItems: 'center' }}>
                            <AddIcon />
                            <Typography style={{ fontSize: '8px', marginTop: '-12px' }}>New</Typography>
                            </Box>  
                        </Fab>
                    </Grid>

                    <Grid item xs={9} sm={10}>
                        <TextField
                            fullWidth
                            style={{ backgroundColor: 'white' }}
                            margin="dense"
                            variant="outlined"
                            name="searchText"
                            id="search-text"
                            placeholder="Search Automations"
                            value={searchText}
                            InputProps={{
                                startAdornment: <SearchIcon color="disabled" style={{ paddingRight: '5px' }} />,
                            }}
                            onChange={({ target: { value } }): void => {
                                handleSearch(value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={1}>

                    </Grid>
                </Grid>

            </Box>
            <Box mt={2}>
                <Grid container>
                    <Grid item xs={11}>
                        <AutomationSettingListing
                            ref={listingRef}
                            editAutomationSetting={EditSetting}
                            searchText={debouncedSearchText}
                            automationFilters={automationFilters}
                        />

                    </Grid>
                    <Grid item xs={1}>
                        <Box mb={10}  display="flex" justifyContent="flex-end">
                            <Fab
                                color="primary"
                                onClick={() => {
                                    setIsFilterOpen(true);
                                }}
                                title="Filter">
                                <Box sx={{ flexDirection: 'column', alignItems: 'center' }}>
                                    <FilterAltIcon />
                                    <Typography style={{ fontSize: '8px', marginTop: '-12px' }}>Filter</Typography>
                                </Box>
                            </Fab>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {addEditSetting &&
                <AddEditAutomation
                    isOpen={isDrawerOpen}
                    onClose={closeEditDrawer}
                    initialValues={addUpdateSetting}
                    allLocations={locationOptions?.filter((item: any) => item.dealerLocationId > 0)}
                    allDepartments={departments||[]}
                    allTaskTypes={taskTypes}
                    dataWOStatus={dataWOStatus}
                    allUsers={dealerUsers}
                    addUpdateLoading={addUpdateLoading}
                    deleteBtnLoading={deleteBtnLoading}
                    isExistingStatus={(status, dealerId) =>
                        listingRef.current?.isExistingStatus?.(status, dealerId) ?? false
                    }
                    onSubmit={AddUpadteAutomationSetting}
                    DeleteAutomationSetting={DeleteAutomationSetting}
                >
                </AddEditAutomation>
            }
            {isFilterOpen &&
                <AutomationFilterDrawer
                    allLocations={locationOptions?.filter((item: any) => item.dealerLocationId > 0)}
                    allDepartments={departments || []}
                    dataWOStatus={dataWOStatus}
                    automationFilters={automationFilters}
                    onSubmit={(values: any) => {
                        setAutomationFilters(values);
                        setIsFilterOpen(false);
                    }}
                    onclose={() => {
                        setIsFilterOpen(false);
                    }}
                />}

        </>

    )
}

export default TextAutomation;


